import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { productsCatalogService, servicesCatalogService } from '../../services/catalogService';
import { useToast } from '../../contexts/ToastContext';

// ── Quality Scoring Logic ────────────────────────────────────

const PRODUCT_REQUIRED_FIELDS = ['productName', 'category', 'oemBrand', 'modelSku', 'licensingModel', 'description', 'priceRange', 'marginCategory'];
const SERVICE_REQUIRED_FIELDS = ['serviceName', 'serviceCategory', 'deliveryModel', 'description', 'industryAlignment'];

function scoreProduct(p) {
  let score = 0;
  const issues = [];
  PRODUCT_REQUIRED_FIELDS?.forEach(f => {
    if (p?.[f] && String(p?.[f])?.trim() !== '') score += 1;
    else issues?.push(f);
  });
  const pct = Math.round((score / PRODUCT_REQUIRED_FIELDS?.length) * 100);
  return { score: pct, missingFields: issues };
}

function scoreService(s) {
  let score = 0;
  const issues = [];
  SERVICE_REQUIRED_FIELDS?.forEach(f => {
    if (s?.[f] && String(s?.[f])?.trim() !== '') score += 1;
    else issues?.push(f);
  });
  const pct = Math.round((score / SERVICE_REQUIRED_FIELDS?.length) * 100);
  return { score: pct, missingFields: issues };
}

function detectProductIssues(items) {
  const nameMap = {};
  const skuMap = {};
  items?.forEach(p => {
    const name = p?.productName?.toLowerCase()?.trim();
    const sku = p?.modelSku?.toLowerCase()?.trim();
    if (name) nameMap[name] = (nameMap?.[name] || [])?.concat(p?.id);
    if (sku) skuMap[sku] = (skuMap?.[sku] || [])?.concat(p?.id);
  });
  const duplicateIds = new Set();
  Object.values(nameMap)?.forEach(ids => { if (ids?.length > 1) ids?.forEach(id => duplicateIds?.add(id)); });
  Object.values(skuMap)?.forEach(ids => { if (ids?.length > 1) ids?.forEach(id => duplicateIds?.add(id)); });
  return duplicateIds;
}

function detectServiceIssues(items) {
  const nameMap = {};
  items?.forEach(s => {
    const name = s?.serviceName?.toLowerCase()?.trim();
    if (name) nameMap[name] = (nameMap?.[name] || [])?.concat(s?.id);
  });
  const duplicateIds = new Set();
  Object.values(nameMap)?.forEach(ids => { if (ids?.length > 1) ids?.forEach(id => duplicateIds?.add(id)); });
  return duplicateIds;
}

function isOutdated(item) {
  if (!item?.updatedAt && !item?.createdAt) return false;
  const date = new Date(item?.updatedAt || item?.createdAt);
  const monthsAgo = (Date.now() - date?.getTime()) / (1000 * 60 * 60 * 24 * 30);
  return monthsAgo > 12;
}

function getIssueType(item, isDuplicate, scoreData) {
  const types = [];
  if (isDuplicate) types?.push('duplicate');
  if (scoreData?.score < 60) types?.push('incomplete');
  if (isOutdated(item)) types?.push('outdated');
  return types;
}

// ── Score Badge ──────────────────────────────────────────────

const ScoreBadge = ({ score }) => {
  const color = score >= 80 ? 'bg-emerald-100 text-emerald-700' : score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />
      {score}%
    </span>
  );
};

// ── Issue Tag ────────────────────────────────────────────────

const IssueTag = ({ type }) => {
  const map = {
    duplicate: { label: 'Duplicate', cls: 'bg-purple-100 text-purple-700', icon: 'Copy' },
    incomplete: { label: 'Incomplete', cls: 'bg-amber-100 text-amber-700', icon: 'AlertCircle' },
    outdated: { label: 'Outdated', cls: 'bg-slate-100 text-slate-600', icon: 'Clock' },
  };
  const cfg = map?.[type] || {};
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.cls}`}>
      <Icon name={cfg?.icon} size={10} />
      {cfg?.label}
    </span>
  );
};

// ── Score Bar ────────────────────────────────────────────────

const ScoreBar = ({ score }) => {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-400' : 'bg-red-500';
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5">
      <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${score}%` }} />
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────

const ISSUE_FILTER_OPTIONS = [
  { value: '', label: 'All Issues' },
  { value: 'duplicate', label: 'Duplicates' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'outdated', label: 'Outdated' },
];

const SCORE_FILTER_OPTIONS = [
  { value: '', label: 'All Scores' },
  { value: 'critical', label: 'Critical (< 50%)' },
  { value: 'warning', label: 'Warning (50–79%)' },
  { value: 'good', label: 'Good (≥ 80%)' },
];

const PAGE_SIZE = 15;

const CatalogDataQuality = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [issueFilter, setIssueFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [bulkAction, setBulkAction] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prods, svcs] = await Promise.all([
        productsCatalogService?.getAll(),
        servicesCatalogService?.getAll(),
      ]);
      setProducts(prods || []);
      setServices(svcs || []);
    } catch (e) {
      toast?.error?.('Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  // ── Enriched items ──────────────────────────────────────────

  const enrichedProducts = useMemo(() => {
    const dupIds = detectProductIssues(products);
    return products?.map(p => {
      const scoreData = scoreProduct(p);
      const isDup = dupIds?.has(p?.id);
      const issues = getIssueType(p, isDup, scoreData);
      return { ...p, _score: scoreData?.score, _missing: scoreData?.missingFields, _issues: issues };
    });
  }, [products]);

  const enrichedServices = useMemo(() => {
    const dupIds = detectServiceIssues(services);
    return services?.map(s => {
      const scoreData = scoreService(s);
      const isDup = dupIds?.has(s?.id);
      const issues = getIssueType(s, isDup, scoreData);
      return { ...s, _score: scoreData?.score, _missing: scoreData?.missingFields, _issues: issues };
    });
  }, [services]);

  const activeItems = activeTab === 'products' ? enrichedProducts : enrichedServices;

  // ── Summary stats ───────────────────────────────────────────

  const stats = useMemo(() => {
    let items = activeItems;
    const total = items?.length;
    const duplicates = items?.filter(i => i?._issues?.includes('duplicate'))?.length;
    const incomplete = items?.filter(i => i?._issues?.includes('incomplete'))?.length;
    const outdated = items?.filter(i => i?._issues?.includes('outdated'))?.length;
    const avgScore = total > 0 ? Math.round(items?.reduce((s, i) => s + i?._score, 0) / total) : 0;
    const healthy = items?.filter(i => i?._issues?.length === 0)?.length;
    return { total, duplicates, incomplete, outdated, avgScore, healthy };
  }, [activeItems]);

  // ── Filtered items ──────────────────────────────────────────

  const filtered = useMemo(() => {
    let items = activeItems;
    if (search) {
      const q = search?.toLowerCase();
      items = items?.filter(i =>
        (i?.productName || i?.serviceName || '')?.toLowerCase()?.includes(q) ||
        (i?.category || i?.serviceCategory || '')?.toLowerCase()?.includes(q) ||
        (i?.oemBrand || '')?.toLowerCase()?.includes(q) ||
        (i?.modelSku || '')?.toLowerCase()?.includes(q)
      );
    }
    if (issueFilter) items = items?.filter(i => i?._issues?.includes(issueFilter));
    if (scoreFilter === 'critical') items = items?.filter(i => i?._score < 50);
    else if (scoreFilter === 'warning') items = items?.filter(i => i?._score >= 50 && i?._score < 80);
    else if (scoreFilter === 'good') items = items?.filter(i => i?._score >= 80);
    return items;
  }, [activeItems, search, issueFilter, scoreFilter]);

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Selection ───────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next?.has(id) ? next?.delete(id) : next?.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected?.size === paginated?.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map(i => i.id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  // ── Bulk actions ────────────────────────────────────────────

  const handleBulkApply = () => {
    if (!bulkAction || selected?.size === 0) return;
    const actionLabels = {
      archive: 'Archive',
      markDraft: 'Mark as Draft',
      delete: 'Delete',
    };
    setConfirmModal({
      action: bulkAction,
      label: actionLabels?.[bulkAction],
      count: selected?.size,
    });
  };

  const executeBulkAction = async () => {
    if (!confirmModal) return;
    setProcessing(true);
    try {
      const ids = Array.from(selected);
      const svc = activeTab === 'products' ? productsCatalogService : servicesCatalogService;
      if (confirmModal?.action === 'delete') {
        await Promise.all(ids?.map(id => svc?.delete(id)));
        toast?.success?.(`${ids?.length} item(s) deleted`);
      } else if (confirmModal?.action === 'archive') {
        await Promise.all(ids?.map(id => svc?.update(id, { catalogStatus: 'sunset' })));
        toast?.success?.(`${ids?.length} item(s) archived`);
      } else if (confirmModal?.action === 'markDraft') {
        await Promise.all(ids?.map(id => svc?.update(id, { catalogStatus: 'draft' })));
        toast?.success?.(`${ids?.length} item(s) marked as Draft`);
      }
      await loadData();
      clearSelection();
      setBulkAction('');
    } catch (e) {
      toast?.error?.('Bulk action failed. Please try again.');
    } finally {
      setProcessing(false);
      setConfirmModal(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelected(new Set());
    setPage(1);
    setSearch('');
    setIssueFilter('');
    setScoreFilter('');
  };

  const getName = (item) => item?.productName || item?.serviceName || '—';
  const getCategory = (item) => item?.category || item?.serviceCategory || '—';

  const formatFieldName = (f) => f?.replace(/([A-Z])/g, ' $1')?.replace(/^./, s => s?.toUpperCase());

  return (
    <>
      <Helmet>
        <title>Catalog Data Quality | PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-[148px]">

          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <button onClick={() => navigate('/products-catalog')} className="hover:text-foreground transition-colors">Catalogs</button>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground font-medium">Data Quality</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Catalog Data Quality</h1>
                <p className="text-muted-foreground text-sm mt-0.5">Identify duplicates, incomplete records, and outdated entries — then clean up in bulk.</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadData} className="shrink-0">
                <Icon name="RefreshCw" size={14} className="mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-muted rounded-lg p-1 w-fit">
            {['products', 'services']?.map(tab => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${activeTab === tab ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'products' ? 'Products' : 'Services'}
              </button>
            ))}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total', value: stats?.total, icon: 'Database', color: 'text-slate-600', bg: 'bg-slate-50' },
              { label: 'Avg Score', value: `${stats?.avgScore}%`, icon: 'BarChart2', color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Healthy', value: stats?.healthy, icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Duplicates', value: stats?.duplicates, icon: 'Copy', color: 'text-purple-600', bg: 'bg-purple-50' },
              { label: 'Incomplete', value: stats?.incomplete, icon: 'AlertCircle', color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Outdated', value: stats?.outdated, icon: 'Clock', color: 'text-slate-500', bg: 'bg-slate-50' },
            ]?.map(card => (
              <div key={card?.label} className={`${card?.bg} rounded-xl p-3 border border-border`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={card?.icon} size={14} className={card?.color} />
                  <span className="text-xs text-muted-foreground">{card?.label}</span>
                </div>
                <div className={`text-xl font-bold ${card?.color}`}>{card?.value}</div>
              </div>
            ))}
          </div>

          {/* Filters + Bulk Actions Bar */}
          <div className="bg-card border border-border rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <Input
                placeholder="Search by name, SKU, brand..."
                value={search}
                onChange={e => { setSearch(e?.target?.value); setPage(1); }}
                className="h-9 text-sm"
              />
            </div>
            <div className="w-40">
              <Select
                options={ISSUE_FILTER_OPTIONS}
                value={issueFilter}
                onChange={v => { setIssueFilter(v); setPage(1); }}
                placeholder="Issue Type"
              />
            </div>
            <div className="w-44">
              <Select
                options={SCORE_FILTER_OPTIONS}
                value={scoreFilter}
                onChange={v => { setScoreFilter(v); setPage(1); }}
                placeholder="Score Range"
              />
            </div>
            {selected?.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-muted-foreground">{selected?.size} selected</span>
                <div className="w-44">
                  <Select
                    options={[
                      { value: '', label: 'Bulk Action…' },
                      { value: 'markDraft', label: 'Mark as Draft' },
                      { value: 'archive', label: 'Archive (Sunset)' },
                      { value: 'delete', label: 'Delete' },
                    ]}
                    value={bulkAction}
                    onChange={setBulkAction}
                    placeholder="Bulk Action…"
                  />
                </div>
                <Button
                  size="sm"
                  variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                  onClick={handleBulkApply}
                  disabled={!bulkAction}
                >
                  Apply
                </Button>
                <button onClick={clearSelection} className="text-muted-foreground hover:text-foreground text-xs underline">Clear</button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                Loading catalog data…
              </div>
            ) : filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <Icon name="CheckCircle" size={40} className="text-emerald-400 mb-3" />
                <p className="font-medium text-foreground">No issues found</p>
                <p className="text-sm mt-1">All catalog entries match the current filter criteria.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="w-10 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={paginated?.length > 0 && selected?.size === paginated?.length}
                            onChange={toggleAll}
                            className="rounded border-border"
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Category</th>
                        {activeTab === 'products' && <th className="px-4 py-3 text-left font-semibold text-muted-foreground">SKU / Brand</th>}
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Status</th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Issues</th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground w-36">Quality Score</th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated?.map((item, idx) => (
                        <tr
                          key={item?.id}
                          className={`border-b border-border last:border-0 transition-colors ${selected?.has(item?.id) ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'} hover:bg-muted/30`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selected?.has(item?.id)}
                              onChange={() => toggleSelect(item?.id)}
                              className="rounded border-border"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setDetailItem(item)}
                              className="font-medium text-foreground hover:text-primary transition-colors text-left"
                            >
                              {getName(item)}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground capitalize">{getCategory(item)}</span>
                          </td>
                          {activeTab === 'products' && (
                            <td className="px-4 py-3 text-muted-foreground text-xs">
                              <div>{item?.modelSku || <span className="text-red-400 italic">No SKU</span>}</div>
                              <div>{item?.oemBrand || <span className="text-red-400 italic">No Brand</span>}</div>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                              item?.catalogStatus === 'active' ? 'bg-emerald-100 text-emerald-700' :
                              item?.catalogStatus === 'sunset'? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {item?.catalogStatus || 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {item?._issues?.length === 0
                                ? <span className="text-xs text-emerald-600 flex items-center gap-1"><Icon name="CheckCircle" size={11} /> Clean</span>
                                : item?._issues?.map(t => <IssueTag key={t} type={t} />)
                              }
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ScoreBar score={item?._score} />
                              <ScoreBadge score={item?._score} />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setDetailItem(item)}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="View details"
                              >
                                <Icon name="Eye" size={14} />
                              </button>
                              {activeTab === 'products' && (
                                <button
                                  onClick={() => navigate(`/products-catalog/${item?.id}`)}
                                  className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                  title="Open product profile"
                                >
                                  <Icon name="ExternalLink" size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-border flex items-center justify-between text-sm text-muted-foreground">
                    <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered?.length)} of {filtered?.length}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40 transition-colors"
                      >
                        <Icon name="ChevronLeft" size={14} />
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const p = i + 1;
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`w-7 h-7 rounded text-xs font-medium transition-colors ${page === p ? 'bg-primary text-white' : 'hover:bg-muted'}`}
                          >
                            {p}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40 transition-colors"
                      >
                        <Icon name="ChevronRight" size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {/* Detail Modal */}
      {detailItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetailItem(null)}>
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{getName(detailItem)}</h2>
                <p className="text-sm text-muted-foreground mt-0.5 capitalize">{getCategory(detailItem)}</p>
              </div>
              <button onClick={() => setDetailItem(null)} className="p-1.5 rounded hover:bg-muted text-muted-foreground">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Score */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-foreground">Quality Score</span>
                  <ScoreBadge score={detailItem?._score} />
                </div>
                <ScoreBar score={detailItem?._score} />
              </div>

              {/* Issues */}
              {detailItem?._issues?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Detected Issues</p>
                  <div className="flex flex-wrap gap-2">
                    {detailItem?._issues?.map(t => <IssueTag key={t} type={t} />)}
                  </div>
                </div>
              )}

              {/* Missing fields */}
              {detailItem?._missing?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Missing Fields ({detailItem?._missing?.length})</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {detailItem?._missing?.map(f => (
                      <div key={f} className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                        <Icon name="AlertCircle" size={11} />
                        {formatFieldName(f)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Populated fields */}
              <div>
                <p className="text-sm font-semibold text-foreground mb-2">Record Details</p>
                <div className="space-y-1.5">
                  {[
                    ['Name', getName(detailItem)],
                    ['Category', getCategory(detailItem)],
                    ['Status', detailItem?.catalogStatus],
                    ['Brand / OEM', detailItem?.oemBrand],
                    ['SKU', detailItem?.modelSku],
                    ['Licensing', detailItem?.licensingModel],
                    ['Price Range', detailItem?.priceRange],
                    ['Margin', detailItem?.marginCategory],
                    ['Description', detailItem?.description],
                    ['Industry', detailItem?.targetIndustry || detailItem?.industryAlignment],
                    ['Delivery Model', detailItem?.deliveryModel],
                    ['Last Updated', detailItem?.updatedAt ? new Date(detailItem.updatedAt)?.toLocaleDateString() : null],
                  ]?.filter(([, v]) => v)?.map(([label, value]) => (
                    <div key={label} className="flex gap-2 text-sm">
                      <span className="text-muted-foreground w-28 shrink-0">{label}</span>
                      <span className="text-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-border flex gap-2 justify-end">
              {activeTab === 'products' && (
                <Button variant="outline" size="sm" onClick={() => { navigate(`/products-catalog/${detailItem?.id}`); setDetailItem(null); }}>
                  <Icon name="ExternalLink" size={13} className="mr-1.5" />
                  Open Profile
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setDetailItem(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${confirmModal?.action === 'delete' ? 'bg-red-100' : 'bg-amber-100'}`}>
                <Icon name={confirmModal?.action === 'delete' ? 'Trash2' : 'AlertTriangle'} size={18} className={confirmModal?.action === 'delete' ? 'text-red-600' : 'text-amber-600'} />
              </div>
              <div>
                <h3 className="font-bold text-foreground">{confirmModal?.label} Items</h3>
                <p className="text-sm text-muted-foreground">{confirmModal?.count} item(s) selected</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              {confirmModal?.action === 'delete'
                ? `This will permanently delete ${confirmModal?.count} catalog item(s). This action cannot be undone.`
                : `This will ${confirmModal?.label?.toLowerCase()} ${confirmModal?.count} catalog item(s).`}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setConfirmModal(null)} disabled={processing}>Cancel</Button>
              <Button
                size="sm"
                variant={confirmModal?.action === 'delete' ? 'destructive' : 'default'}
                onClick={executeBulkAction}
                disabled={processing}
              >
                {processing ? <Icon name="Loader2" size={13} className="animate-spin mr-1.5" /> : null}
                Confirm {confirmModal?.label}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CatalogDataQuality;
